import React, { useId } from "react";
import {
  getOverlayPreserveAspectRatio,
  toOverlayCorners,
} from "./DocumentCoordinates";
import { createTargetFrame } from "./DocumentGeometry";

const toPoints = (corners) =>
  corners.map((corner) => `${corner.x},${corner.y}`).join(" ");

const pointToward = (origin, target, distance) => {
  const edgeLength = Math.hypot(target.x - origin.x, target.y - origin.y);
  const ratio = Math.min(distance / Math.max(edgeLength, 1), 0.28);

  return {
    x: origin.x + (target.x - origin.x) * ratio,
    y: origin.y + (target.y - origin.y) * ratio,
  };
};

const getCornerPath = (corners, index, markerLength) => {
  const corner = corners[index];
  const previous = corners[(index + corners.length - 1) % corners.length];
  const next = corners[(index + 1) % corners.length];
  const previousPoint = pointToward(corner, previous, markerLength);
  const nextPoint = pointToward(corner, next, markerLength);

  return `M ${previousPoint.x} ${previousPoint.y} L ${corner.x} ${corner.y} L ${nextPoint.x} ${nextPoint.y}`;
};

/**
 * 检测结果的纯展示层，不参与合规判断。
 * 没有可追踪四角时画中央目标；有四角时画多边形和四个 L 形标记。
 */
export function DetectionOverlay({
  detection,
  status = "SEARCHING",
  objectFit = "contain",
}) {
  const overlayId = `document-overlay-${useId().replace(/:/g, "")}`;
  const frameAspectRatio = Math.max(detection?.frameAspectRatio || 4 / 3, 0.01);

  // 只有追踪器仍持有完整证件四角时才绘制追踪框。
  // 短暂漏检会由 DocumentTracker 保留旧四角；超过容忍帧数、少于四角或证件出画后，
  // 这里统一切回中央默认四角，避免残缺边框。
  const hasDetectedCorners =
    detection?.detected &&
    detection?.complete &&
    detection?.corners?.length === 4;
  const corners = hasDetectedCorners
    ? toOverlayCorners(detection.corners, frameAspectRatio)
    : [];
  const isValid = detection?.valid || ["GOOD", "RECORDING"].includes(status);
  const normalizedTargetCorners =
    detection?.targetFrame?.corners || createTargetFrame().corners;
  const targetCorners = toOverlayCorners(
    normalizedTargetCorners,
    frameAspectRatio,
  );
  const showTarget = !hasDetectedCorners;
  const isSearching = showTarget && status !== "MANUAL";
  const stroke = isValid ? "#34d399" : "#c79a45";
  const markerLength = 0.055;
  const searchDistance = targetCorners[3].y - targetCorners[0].y;

  return (
    <svg
      className="document-detection-overlay pointer-events-none absolute inset-0 size-full"
      viewBox={`0 0 ${frameAspectRatio} 1`}
      preserveAspectRatio={getOverlayPreserveAspectRatio(objectFit)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${overlayId}-scan`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5eead4" stopOpacity="0" />
          <stop offset="20%" stopColor="#5eead4" stopOpacity="0.9" />
          <stop offset="80%" stopColor="#5eead4" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#5eead4" stopOpacity="0" />
        </linearGradient>
      </defs>
      {showTarget &&
        targetCorners.map((_, index) => (
          <path
            key={`target-${index}`}
            className="document-target-corner"
            d={getCornerPath(targetCorners, index, markerLength)}
            fill="none"
            stroke="rgba(255,255,255,0.96)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      {isSearching && (
        <line
          className="document-search-scan-line"
          x1={targetCorners[0].x}
          y1={targetCorners[0].y}
          x2={targetCorners[1].x}
          y2={targetCorners[1].y}
          stroke={`url(#${overlayId}-scan)`}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            values={`0 0; 0 ${searchDistance}; 0 0`}
            dur="1.8s"
            repeatCount="indefinite"
          />
        </line>
      )}
      {hasDetectedCorners && (
        <polygon
          className={`document-detected-outline ${
            isValid
              ? "document-detected-outline--valid"
              : "document-detected-outline--invalid"
          }`}
          points={toPoints(corners)}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {corners.map((_, index) => (
        <path
          key={index}
          className={`document-corner-marker document-corner-marker--tracked ${
            isValid
              ? "document-corner-marker--valid"
              : "document-corner-marker--invalid"
          }`}
          d={getCornerPath(corners, index, markerLength)}
          fill="none"
          stroke={stroke}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}
